// Event form component
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { eventSchema, type EventInput } from '../../../shared/types'
import { useTranslation } from '../../hooks/useTranslation'
import { OwnerSelect } from './OwnerSelect'

interface Props {
  defaultValues?: Partial<EventInput>
  onSubmit: (data: EventInput) => void
  onCancel?: () => void
  loading?: boolean
  canWriteField?: (field: string) => boolean
}

export function EventForm({ defaultValues, onSubmit, onCancel, loading, canWriteField = () => true }: Props) {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EventInput>({
    resolver: zodResolver(eventSchema),
    defaultValues,
  })

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
          {t('fields.subject')} <span className="text-red-500">*</span>
        </label>
        <input
          {...register('subject')}
          type="text"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
        {errors.subject && (
          <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="startTime" className="block text-sm font-medium text-gray-700">
            {t('fields.startTime')} <span className="text-red-500">*</span>
          </label>
          <input
            {...register('startTime')}
            type="datetime-local"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.startTime && (
            <p className="mt-1 text-sm text-red-600">{errors.startTime.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="endTime" className="block text-sm font-medium text-gray-700">
            {t('fields.endTime')} <span className="text-red-500">*</span>
          </label>
          <input
            {...register('endTime')}
            type="datetime-local"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.endTime && (
            <p className="mt-1 text-sm text-red-600">{errors.endTime.message}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="type" className="block text-sm font-medium text-gray-700">
            {t('fields.type')} <span className="text-red-500">*</span>
          </label>
          <select
            {...register('type')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          >
            <option value="">{t('common.select')}</option>
            <option value="call">{t('status.call')}</option>
            <option value="meeting">{t('status.meeting')}</option>
            <option value="email">{t('fields.email')}</option>
          </select>
          {errors.type && (
            <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
          )}
        </div>

        {canWriteField('status') && (
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              {t('fields.status')}
            </label>
            <select
              {...register('status')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            >
              <option value="scheduled">{t('status.scheduled')}</option>
              <option value="completed">{t('status.completed')}</option>
              <option value="cancelled">{t('status.cancelled')}</option>
            </select>
            {errors.status && (
              <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
            )}
          </div>
        )}
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
          {t('fields.description')}
        </label>
        <textarea
          {...register('description')}
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="relatedToType" className="block text-sm font-medium text-gray-700">
            {t('fields.relatedToType')}
          </label>
          <select
            {...register('relatedToType')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          >
            <option value="">{t('common.select')}</option>
            <option value="lead">{t('status.lead')}</option>
            <option value="account">{t('status.account')}</option>
            <option value="contact">{t('status.contact')}</option>
            <option value="opportunity">{t('status.opportunity')}</option>
          </select>
          {errors.relatedToType && (
            <p className="mt-1 text-sm text-red-600">{errors.relatedToType.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="relatedToId" className="block text-sm font-medium text-gray-700">
            {t('fields.relatedToId')}
          </label>
          <input
            {...register('relatedToId')}
            type="text"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.relatedToId && (
            <p className="mt-1 text-sm text-red-600">{errors.relatedToId.message}</p>
          )}
        </div>
      </div>

      {/* Owner assignment - only visible to admins/managers */}
      <Controller
        name="ownerId"
        control={control}
        render={({ field }) => (
          <OwnerSelect
            value={field.value}
            onChange={field.onChange}
            disabled={loading}
          />
        )}
      />

      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {loading ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  )
}
